// Session-scoped staged-rule-changes registry.
//
// Bill of Operations releases are driven by counts on the matrix DTO
// (`unreleased_overrides_count`, `work_orders_behind_count`). Adding or
// deleting a relevance rule changes NEITHER count server-side, so rule
// edits are invisible to those counts. This module records rule changes in
// a module-level registry so the AffordanceBar can surface "you have staged
// rule changes to release" until the user releases (or reloads — the
// registry is deliberately not persisted across reloads).
//
// The store is exposed through `useSyncExternalStore`, so the snapshot
// objects it returns must be referentially stable between changes (React
// loops forever otherwise). We cache the computed snapshot per partner and
// invalidate that cache only when the underlying counts mutate.

import { useSyncExternalStore } from 'react';

/** operationId → number of staged rule changes recorded for it. */
type OperationCounts = Map<string, number>;

/** partnerId → per-operation staged counts. */
const registry = new Map<string, OperationCounts>();

const listeners = new Set<() => void>();

export interface StagedRuleChangesSnapshot {
  /** Total staged rule changes across all operations for the partner. */
  total: number;
  /** Operation ids (sorted) that have at least one staged rule change. */
  operationIds: string[];
}

// A single frozen empty snapshot shared by every partner with no staged
// changes — guarantees referential stability for the common (empty) case
// and for an undefined partner id.
const EMPTY_SNAPSHOT: StagedRuleChangesSnapshot = { total: 0, operationIds: [] };

// Cached snapshot per partner; recomputed lazily and invalidated on write.
const snapshotCache = new Map<string, StagedRuleChangesSnapshot>();

function emitChange(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function invalidate(partnerId: string): void {
  snapshotCache.delete(partnerId);
}

function computeSnapshot(partnerId: string): StagedRuleChangesSnapshot {
  const counts = registry.get(partnerId);
  if (!counts || counts.size === 0) return EMPTY_SNAPSHOT;

  let total = 0;
  const operationIds: string[] = [];
  for (const [operationId, count] of counts) {
    if (count > 0) {
      total += count;
      operationIds.push(operationId);
    }
  }
  if (operationIds.length === 0) return EMPTY_SNAPSHOT;

  operationIds.sort();
  return { total, operationIds };
}

function getSnapshot(partnerId: string | undefined): StagedRuleChangesSnapshot {
  if (!partnerId) return EMPTY_SNAPSHOT;
  const cached = snapshotCache.get(partnerId);
  if (cached) return cached;
  const snapshot = computeSnapshot(partnerId);
  snapshotCache.set(partnerId, snapshot);
  return snapshot;
}

// ─── Mutations ───────────────────────────────────────────────────────

/** Record one staged rule change (create or delete) for an operation. */
export function recordStagedRuleChange(partnerId: string, operationId: string): void {
  let counts = registry.get(partnerId);
  if (!counts) {
    counts = new Map();
    registry.set(partnerId, counts);
  }
  counts.set(operationId, (counts.get(operationId) ?? 0) + 1);
  invalidate(partnerId);
  emitChange();
}

/**
 * Clear staged rule changes for a partner. Pass `operationIds` to clear
 * only those operations (e.g. the ones just released); omit it to clear
 * every staged change for the partner.
 */
export function clearStagedRuleChanges(partnerId: string, operationIds?: string[]): void {
  const counts = registry.get(partnerId);
  if (!counts) return;

  if (!operationIds) {
    registry.delete(partnerId);
  } else {
    for (const operationId of operationIds) counts.delete(operationId);
    if (counts.size === 0) registry.delete(partnerId);
  }
  invalidate(partnerId);
  emitChange();
}

/** Test-only: wipe the entire registry and snapshot cache. */
export function resetStagedRuleChanges(): void {
  registry.clear();
  snapshotCache.clear();
  emitChange();
}

// ─── Hooks ───────────────────────────────────────────────────────────

/** Subscribe to the partner's staged rule changes (stable snapshot). */
export function useStagedRuleChanges(partnerId: string | undefined): StagedRuleChangesSnapshot {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot(partnerId),
    () => getSnapshot(partnerId),
  );
}

/** Subscribe to the staged rule-change count for a single operation. */
export function useOperationStagedRuleCount(
  partnerId: string | undefined,
  operationId: string,
): number {
  const read = () => (partnerId ? (registry.get(partnerId)?.get(operationId) ?? 0) : 0);
  return useSyncExternalStore(subscribe, read, read);
}
